// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useState } from 'react';

import type { HubData, ProjectData } from '@aps_sdk/data-management';
import { Loader } from '@mantine/core';

import { useAutodeskAuth } from 'components/Autodesk/auth/useAutodeskAuth';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';
import { hasLengthExactly, type WithRequired } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';

type Props = {
  selectedHub: NonNullable<HubData['id']> | null;
  setSelectedHub: (hub: Props['selectedHub']) => void;
  selectedAutodeskProject: NonNullable<ProjectData['id']> | null;
  setSelectedAutodeskProject: (project: Props['selectedAutodeskProject']) => void;
};

export const AutodeskProjectPicker = ({
  selectedAutodeskProject,
  setSelectedAutodeskProject,
  selectedHub,
  setSelectedHub,
}: Props) => {
  const { dataClient } = useAutodeskAuth();

  const [hubs, setHubs] = useState<WithRequired<HubData, 'id'>[]>();
  const [loadingHubs, setLoadingHubs] = useState(true);
  const [autodeskProjects, setAutodeskProjects] = useState<WithRequired<ProjectData, 'id'>[]>();
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    setLoadingHubs(true);
    void dataClient
      .getHubs()
      .then((d) => d.data?.filter((hub): hub is WithRequired<HubData, 'id'> => isNotNil(hub.id)))
      .then((data) => {
        setHubs(data);
        if (isNotNil(data) && hasLengthExactly(data, 1)) {
          setSelectedHub(data[0].id);
        }
      })
      .finally(() => setLoadingHubs(false));
  }, [dataClient, setSelectedHub]);

  useEffect(() => {
    if (isNotNil(selectedHub)) {
      setLoadingProjects(true);
      void dataClient
        .getHubProjects(selectedHub)
        .then((d) => setAutodeskProjects(d.data?.filter((p): p is WithRequired<ProjectData, 'id'> => isNotNil(p.id))))
        .finally(() => setLoadingProjects(false));
    }
  }, [dataClient, selectedHub]);

  return (
    <>
      <WrappedSelect
        label="Autodesk Hub"
        value={selectedHub}
        onChange={(hub) => {
          setSelectedHub(hub);
          setSelectedAutodeskProject(null);
        }}
        flex={1}
        w="auto"
        rightSection={loadingHubs ? <Loader size="xs" /> : undefined}
        data={
          hubs?.map((h) => ({
            label: h.attributes?.name ?? 'Unknown',
            value: h.id,
          })) ?? []
        }
      />
      <WrappedSelect
        label="Autodesk Project"
        disabled={isNil(selectedHub)}
        placeholder={isNil(selectedHub) ? 'Select a Hub' : 'Select a Project...'}
        value={selectedAutodeskProject}
        onChange={setSelectedAutodeskProject}
        rightSection={loadingProjects ? <Loader size="xs" /> : undefined}
        flex={1}
        w="auto"
        data={
          autodeskProjects?.map((h) => ({
            label: h.attributes.name ?? 'Unknown',
            value: h.id,
          })) ?? []
        }
        searchable
      />
    </>
  );
};
